import React from 'react';
import { useSelector } from 'react-redux';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { StepContainer } from '../../components/StepContainer/StepContainer';
import { StepFooter } from '../../components/StepFooter/StepFooter';
import { useCharitableGivingContext } from '../../context/useCharitableGivingContext';
import { FormValues, STEP_ID, validation } from './DafGrantYear.meta';
import { INPUTS_WIDTH } from '../../CharitableGiving.meta';
import { useNavigation } from '../../hooks/useNavigation';
import { Select } from '../../../../../CommonComponents/OPL/Select/Select/Select';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { selectCurrentClient } from '../../../../../CommonSelectors/CommonAreaSelector';
import { WorkflowDTO } from '../../api/useCharitableGivingQuery';

export const DafGrantYear = () => {
  const { workflow, setWorkflow, setStep } = useCharitableGivingContext();

  const { progress, previousStep, nextStep, isFirstStep } = useNavigation({ currentStep: STEP_ID });

  const {
    data: { dafGrantStartYear, dafGrantEndYear },
    options: { dafStartYearOptions, dafEndYearOptions },
  } = workflow;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      dafGrantStartYear,
      dafGrantEndYear,
    },
    resolver: yupResolver(validation),
  });

  const onSubmit = handleSubmit(values => {
    const newWorkflow: WorkflowDTO = {
      ...workflow,
      data: {
        ...workflow.data,
        dafGrantStartYear: values.dafGrantStartYear,
        dafGrantEndYear: values.dafGrantEndYear,
      },
    };

    setWorkflow(newWorkflow);

    if (nextStep) {
      setStep(nextStep);
    }
  });

  const { Dob: primaryDob, PartnerDob: spouseDob } = useSelector(selectCurrentClient);

  const primaryYob = primaryDob ? new Date(primaryDob).getFullYear() : null;
  const spouseYob = spouseDob ? new Date(spouseDob).getFullYear() : null;

  const dafStartYearOptionsWithAgeLabel = dafStartYearOptions.map(opt => ({
    ...opt,
    rightLabel:
      primaryYob && opt.value
        ? `Age ${opt.value - primaryYob}${spouseYob ? `, Age ${opt.value - spouseYob}` : ''}`
        : undefined,
  }));

  const dafEndYearOptionsWithAgeLabel = dafEndYearOptions.map(opt => ({
    ...opt,
    rightLabel:
      primaryYob && opt.value
        ? `Age ${opt.value - primaryYob}${spouseYob ? `, Age ${opt.value - spouseYob}` : ''}`
        : undefined,
  }));

  return (
    <StepContainer
      title="When would you like the charity to receive the donation?"
      onSubmit={onSubmit}
      footer={
        <StepFooter
          isFirstStep={isFirstStep}
          progress={progress}
          onPrevious={previousStep ? () => setStep(previousStep) : undefined}
        />
      }
    >
      <Box display="flex" flexDirection="column" style={{ gap: 32 }}>
        <Box display="flex" style={{ gap: 16 }}>
          <Controller
            control={control}
            name="dafGrantStartYear"
            render={({ field: { value, onChange, ref } }) => (
              <Box style={{ width: INPUTS_WIDTH }}>
                <Select
                  innerRef={ref}
                  label={
                    <Box display="flex" alignItems="baseline" style={{ gap: 4 }}>
                      <Text as="span" variant="formLabel">
                        Grant Start Year
                      </Text>
                      <Text as="span" fontSize="small" color="neutral.500">
                        (Optional)
                      </Text>
                    </Box>
                  }
                  value={dafStartYearOptions.find(
                    opt => opt.value === value?.year && opt.type === value?.type,
                  )}
                  options={dafStartYearOptionsWithAgeLabel}
                  onChange={opt => {
                    if (opt) {
                      onChange({ year: opt.value, type: opt.type });
                    }
                  }}
                />
              </Box>
            )}
          />

          <Controller
            control={control}
            name="dafGrantEndYear"
            render={({ field: { value, onChange, ref } }) => (
              <Box style={{ width: INPUTS_WIDTH }}>
                <Select
                  innerRef={ref}
                  label={
                    <Box display="flex" alignItems="baseline" style={{ gap: 4 }}>
                      <Text as="span" variant="formLabel">
                        Grant End Year
                      </Text>
                      <Text as="span" fontSize="small" color="neutral.500">
                        (Optional)
                      </Text>
                    </Box>
                  }
                  value={dafEndYearOptions.find(
                    opt => opt.value === value?.year && opt.type === value?.type,
                  )}
                  options={dafEndYearOptionsWithAgeLabel}
                  onChange={opt => {
                    if (opt) {
                      onChange({ year: opt.value, type: opt.type });
                    }
                  }}
                  formVariant={errors.dafGrantEndYear ? 'error' : undefined}
                  helperText={(errors.dafGrantEndYear as FieldError)?.message}
                />
              </Box>
            )}
          />
        </Box>
      </Box>
    </StepContainer>
  );
};
